import { isObject } from '@iconik-io/core/src/is_object';
import {
  type AssetsCollectionInputSchema,
  type AssetsCollectionInputSchemaPartial,
  type AssetsCollectionSchemaWithIdNullable,
  type AssetsHttpPatchCollectionsByCollectionIdQuery,
  type AssetsHttpPostCollectionsQuery,
  httpDeleteCollectionsByCollectionIdContentsByObjectTypeByObjectId,
  httpGetCollectionsByCollectionId,
  httpGetCollectionsByCollectionIdAncestors,
  httpPatchCollectionsByCollectionId,
  httpPostCollections,
  httpPostCollectionsByCollectionIdContents,
  httpPostCollectionsByCollectionIdSubcollections,
  httpPostCollectionsByCollectionIdViews,
  httpPostDeleteQueueCollections,
  httpPostFavorites,
} from '@iconik-io/iconik-api/src/services/assets';
import {
  httpPostSearchConvertToCollection,
  httpPostSearchSavedBySearchIdConvertToCollection,
  type SearchSavedSearchConvertCollectionSchema,
  type SearchSearchConvertCollectionSchema,
} from '@iconik-io/iconik-api/src/services/search';
import {
  OBJECT_TYPE_ASSETS,
  OBJECT_TYPE_COLLECTIONS,
} from '@iconik-io/iconik-api/src/types/object_types';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import { updateEntities } from '@iconik-io/shared-lib/src/redux/entities/slice';

import { permissions as defaultPermissions } from '../../constants/data';
import {
  ID_COLLECTION as reducerId,
  treeReducer,
} from '../../constants/reducer_ids';
import {
  COLLECTION_PAGE_ROUTE,
  SHARE_COLLECTION_PAGE_ROUTE,
} from '../../constants/routes';
import type { AppDispatch, GetState } from '../../store/types';
import checkPermission from '../../utils/check_permission';
import { readField, readFieldText } from '../../utils/read_field';
import { UUID_ZERO } from '../../utils/tree_handlers';
import { segmentTrackEvent } from '../analytics_tracking/thunks';
import type { AuthUserData } from '../auth/types';
import {
  dragAndDropKeyframesOriginalSource,
  dragAndDropKeyframesSuccess,
  dragAndDropKeyframesWaiting,
} from '../drag_and_drop_keyframes/slice';
import { historyGoBack, historyPush, historyReplace } from '../history/actions';
import { errorMessage, successMessage } from '../messages/thunks';
import { setFlashHighlightIds } from '../pages/collection/slice';
import { searchActions } from '../pages/search/actions';
import { getSearchRequestBody } from '../search/thunks';
import {
  addBranches,
  modifyBranchByID,
  removeBranchByID,
} from '../tree/thunks';
import type { TreeReducerId } from '../tree/types';

export function deleteCollectionsByIds(ids: string[]) {
  return (dispatch: AppDispatch, getState: GetState) =>
    dispatch(httpPostDeleteQueueCollections({ ids }))
      .then(() => {
        const routeParams = getState().routeParams;
        // this condition handles redirect when a user deletes a collection which is the same as the collection page s/he is at
        if (ids.includes(getState().routeParams.params.id)) {
          if (routeParams.prevRoute) {
            dispatch(historyGoBack());
          } else {
            dispatch(historyReplace('/search'));
          }
        }
      })
      .then(() => {
        const entityItems = Object.fromEntries(
          ids.map((id) => [id, { status: 'DELETED' as const }]),
        );
        dispatch(
          updateEntities({
            search_items: entityItems,
            collections: entityItems,
          }),
        );

        ids.forEach((entityId) => {
          dispatch(removeBranchByID(reducerId, entityId));

          dispatch(
            segmentTrackEvent({
              eventName: 'Asset Deleted',
              properties: { Type: 'COLLECTION' },
            }),
          );
        });
      });
}

export function createCollection(
  data: AssetsCollectionInputSchema,
  parentId?: string,
  params?: AssetsHttpPostCollectionsQuery,
  collectionTreeReducerId?: TreeReducerId,
) {
  const treeReducerId = collectionTreeReducerId ?? reducerId;
  return async (dispatch: AppDispatch, getState: GetState) => {
    const response = await dispatch(
      httpPostCollections(
        {
          ...data,
          parent_id: parentId,
        },
        params,
      ),
    );

    const state = getState();
    if (state[treeReducer[reducerId]].flatTree.length > 0) {
      dispatch(
        addBranches(treeReducerId, {
          objects: [
            {
              ...response.body,
              object_type: 'collections',
              permissions: defaultPermissions,
            },
          ],
          parent: parentId,
          index: 0,
          checkParentExpanded: true,
        }),
      );
    }

    await dispatch(httpPostCollectionsByCollectionIdViews(response.body.id));

    return response;
  };
}

export function convertSavedSearchToCollection(
  data: SearchSavedSearchConvertCollectionSchema,
  savedSearchId: string,
) {
  return (dispatch: AppDispatch) => {
    return dispatch(
      httpPostSearchSavedBySearchIdConvertToCollection(savedSearchId, data),
    );
  };
}

export function markCollectionAsFavourite(collectionId: string) {
  return async (dispatch: AppDispatch) => {
    await dispatch(
      httpPostFavorites(
        { object_ids: [collectionId], object_type: 'collections' },
        { index_immediately: true },
      ),
    );
    dispatch(
      updateEntities({
        search_items: { [collectionId]: { favoured: true } },
      }),
    );
    dispatch(modifyBranchByID(reducerId, collectionId, { favoured: true }));
  };
}

export function convertSearchResultsToCollection(collectionId: string) {
  return (dispatch: AppDispatch, getState: GetState) => {
    const searchBody = getSearchRequestBody(getState());
    const body: SearchSearchConvertCollectionSchema = {
      ...searchBody,
      collection_id: collectionId,
      doc_types: ['assets'],
    };
    return dispatch(httpPostSearchConvertToCollection(body));
  };
}

export function createAddCollection(
  data: AssetsCollectionInputSchema,
  parentId?: string,
  params?: AssetsHttpPostCollectionsQuery,
) {
  return (dispatch: AppDispatch, getState: GetState) =>
    dispatch(createCollection(data, parentId, params)).then((collection) => {
      const routeParams = getState().routeParams;
      const collectionId = [
        COLLECTION_PAGE_ROUTE,
        SHARE_COLLECTION_PAGE_ROUTE,
      ].includes(routeParams.route)
        ? routeParams.params.id
        : undefined;

      const collectionData = {
        ...collection.body,
        object_type: OBJECT_TYPE_COLLECTIONS,
        permissions: defaultPermissions,
      };

      // condition used to prevent list hot-update on wrong collection
      const isCollectionPage =
        routeParams.route === COLLECTION_PAGE_ROUTE ||
        routeParams.route === SHARE_COLLECTION_PAGE_ROUTE;
      const shouldUpdateResultItems =
        !isCollectionPage || collectionId === collectionData.parent_id;

      if (shouldUpdateResultItems) {
        dispatch(
          updateEntities({
            search_items: { [collectionData.id]: collectionData },
          }),
        );
        dispatch(setFlashHighlightIds([collectionData.id]));
      } else {
        dispatch(historyPush(`/collection/${collectionData.id}`));
      }

      if (shouldUpdateResultItems) {
        dispatch(searchActions.prependId(collectionData.id));
      }

      return collection;
    });
}

// store collection's keyframes and set the state of to loading
export const initCollectionKeyframes =
  (collectionId: string) => (dispatch: AppDispatch, getState: GetState) => {
    const collection = getState().entities.search_items[collectionId];
    if (collection) {
      const modifiedKeyframes =
        getState().dragAndDropKeyframes.originalSource[collection.id];
      if (!modifiedKeyframes) {
        dispatch(
          dragAndDropKeyframesOriginalSource({
            id: collectionId,
            keyframes: collection.keyframes ?? [],
          }),
        );
      }
      dispatch(dragAndDropKeyframesWaiting(collectionId));
    }
  };

// validate items to be added to a collection
export const validateCopyMoveToCollection = (
  destinationId: string,
  permissions: string[],
  parentIds: string[],
  selectedItems: SearchItemEntity[],
  userData: AuthUserData,
  copy = false,
) => {
  const success: SearchItemEntity[] = [];
  const fails: SearchItemEntity[] = [];
  const errors: string[] = [];
  const canWriteDestination = checkPermission(permissions, 'write', userData);

  selectedItems.forEach((item) => {
    // check if the item is already in the collection
    if (item.in_collections?.includes(destinationId)) {
      errors.push(`"${item.title}": already exist in collection`);
      fails.push(item);
      return;
    }

    const canWriteItem = checkPermission(item.permissions, 'write', userData);

    switch (item.object_type) {
      case OBJECT_TYPE_ASSETS:
        if (!canWriteDestination) {
          errors.push(
            `"${item.title}": You don't have permission to write the destination collection`,
          );
          fails.push(item);
          return;
        }
        if (!copy && !canWriteItem) {
          errors.push(
            `"${item.title}": You don't have permission to write the item`,
          );
          fails.push(item);
          return;
        }
        success.push(item);
        break;

      case OBJECT_TYPE_COLLECTIONS:
        if (item.id === destinationId) {
          errors.push("You can't add a collection to itself");
          fails.push(item);
          return;
        }
        if (parentIds.includes(item.id)) {
          errors.push(
            `"${item.title}": Parent collections can't be added as children`,
          );
          fails.push(item);
          return;
        }
        if (!canWriteDestination) {
          errors.push(
            `"${item.title}": You don't have permission to write the destination collection`,
          );
          fails.push(item);
          return;
        }
        if (!copy && !canWriteItem) {
          errors.push(
            `"${item.title}": You don't have permission to write the item`,
          );
          fails.push(item);
          return;
        }
        success.push(item);
        break;

      default: {
        break;
      }
    }
  });
  return { success, fails, errors };
};

export const addCollectionContent =
  (
    collectionId: string,
    item: SearchItemEntity,
    copy: boolean,
    copyCollectionACLs?: boolean,
    collectionTreeReducerId?: TreeReducerId,
  ) =>
  (dispatch: AppDispatch) => {
    if (item.object_type === OBJECT_TYPE_COLLECTIONS) {
      if (collectionId !== item.id) {
        const options = { parent_id: collectionId };
        if (!copy) {
          const treeReducerId = collectionTreeReducerId ?? reducerId;
          dispatch(removeBranchByID(treeReducerId, item.id));
          const objects = [
            {
              ...item,
              in_collections: [collectionId],
            },
          ];
          dispatch(
            addBranches(treeReducerId, {
              objects,
              parent: collectionId,
              index: 0,
              checkParentExpanded: true,
            }),
          );
        }

        if (copy) {
          return dispatch(
            httpPostCollectionsByCollectionIdSubcollections(
              collectionId,
              {
                object_id: item.id,
                object_type: OBJECT_TYPE_COLLECTIONS,
              },
              {
                copy_acl: copyCollectionACLs,
              },
            ),
          );
        }
        return dispatch(httpPatchCollectionsByCollectionId(item.id, options));
      }
    } else {
      return dispatch(
        httpPostCollectionsByCollectionIdContents(
          collectionId,
          {
            object_id: item.id,
            object_type: item.object_type,
          },
          { index_immediately: true },
        ),
      );
    }
    // Nothing to send for an item already in the target collection.
    return Promise.resolve(undefined);
  };

export const deleteCollectionContent =
  (
    collectionId: string,
    entityId: string,
    copy = false,
    collectionTreeReducerId?: TreeReducerId,
  ) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const searchItem = getState().entities.search_items[entityId];
    const entityItems = Object.fromEntries([
      [entityId, { status: 'DELETED' as const }],
    ]);
    dispatch(
      updateEntities({
        search_items: entityItems,
        collections: entityItems,
      }),
    );
    let response;
    if (searchItem.object_type === 'collections') {
      const options: AssetsCollectionInputSchemaPartial = {
        is_root: true,
        parent_id: null,
      };
      const query: AssetsHttpPatchCollectionsByCollectionIdQuery = {};
      const treeReducerId = collectionTreeReducerId ?? reducerId;
      if (copy) {
        query.change_parent_mode = 'copy';
      } else {
        dispatch(removeBranchByID(treeReducerId, entityId));
        const objects = [
          {
            ...searchItem,
            in_collections: [],
          },
        ];
        dispatch(
          addBranches(treeReducerId, { objects, parent: UUID_ZERO, index: 0 }),
        );
      }
      response = dispatch(
        httpPatchCollectionsByCollectionId(entityId, options, query),
      );
    } else if (collectionId !== UUID_ZERO) {
      response = dispatch(
        httpDeleteCollectionsByCollectionIdContentsByObjectTypeByObjectId(
          collectionId,
          searchItem.object_type,
          entityId,
        ),
      );
    }
    // Nothing to send when the item is not in a collection to begin with.
    return response ?? Promise.resolve(undefined);
  };

// requests to add items to a collection
export const getMoveCopyToCollectionRequests =
  (
    collectionId: string,
    items: SearchItemEntity[],
    copy: boolean,
    copyCollectionACLs: boolean | undefined,
  ) =>
  (dispatch: AppDispatch) =>
    Promise.all(
      items.map((item) =>
        dispatch(
          collectionId !== UUID_ZERO
            ? addCollectionContent(collectionId, item, copy, copyCollectionACLs)
            : deleteCollectionContent(collectionId, item.id, copy),
        ),
      ),
    );

export type UpdatedItem = {
  id: string;
  type: string;
};

/**
 * These come from several move and copy endpoints, which agree on neither
 * their request nor their response: deleting contents sends no body at all,
 * patching a collection sends the collection's own fields, and posting
 * contents declares no response. Both ends are therefore read, not assumed.
 */
type MoveCopyResponse = {
  request: object;
  body: unknown;
};

// set new parent and status in items
export function updateSearchItemsAfterMoveCopy(
  id: string,
  responses: (MoveCopyResponse | undefined)[],
  copy = false,
) {
  return (dispatch: AppDispatch, getState: GetState) => {
    const updatedItems: UpdatedItem[] = [];

    responses.forEach((response) => {
      // A cancelled request settles as void, carrying nothing to apply.
      if (!response) {
        return;
      }
      const sent = readField(response.request, 'body');
      const requestBody = isObject(sent) ? sent : {};
      const objectId = readFieldText(requestBody, 'object_id');
      const objectType = readFieldText(requestBody, 'object_type');
      // we need to distinguish assets from collections requests
      const collectionId = isObject(response.body)
        ? readFieldText(response.body, 'id')
        : undefined;
      const elementId = objectType === 'assets' ? objectId : collectionId;
      //
      if (elementId) {
        updatedItems.push({
          id: String(elementId),
          type: objectType ? String(objectType) : 'collections',
        });
        const updateObject = getState().entities.search_items[elementId];
        if (updateObject) {
          const dataUpdate: Partial<typeof updateObject> = {
            in_collections: [...(updateObject.in_collections || []), id],
          };
          if (collectionId && !copy) {
            // mark collection as deleted since the collection is moved
            dataUpdate.status = 'DELETED';
          }
          dispatch(
            updateEntities({ search_items: { [elementId]: dataUpdate } }),
          );
        }
      }
    });
    return updatedItems;
  };
}

export function deleteItemsFromCollection(
  collectionId: string,
  entityIds: string[],
) {
  return (dispatch: AppDispatch) =>
    Promise.all(
      entityIds.map((entityId) =>
        dispatch(deleteCollectionContent(collectionId, entityId)),
      ),
    );
}

export function moveCopyItemsToCollection(
  targetCollectionId: string,
  parents: string[] = [],
  permissions: string[] = [],
  items: SearchItemEntity[] = [],
  copy = false,
  copyCollectionACLs: boolean | undefined = undefined,
) {
  return async (dispatch: AppDispatch, getState: GetState) => {
    // unselect elements
    const userData = getState().authReducer.userData;

    // handle permissions
    const { success, errors } = validateCopyMoveToCollection(
      targetCollectionId,
      permissions,
      parents,
      items,
      userData,
      copy,
    );
    for (const text of errors) {
      dispatch(errorMessage({ text }));
    }

    if (success.length) {
      // handle collection keyframes initial state
      dispatch(initCollectionKeyframes(targetCollectionId));
    }

    // API calls to add items
    const responses = await dispatch(
      getMoveCopyToCollectionRequests(
        targetCollectionId,
        success,
        copy,
        copyCollectionACLs,
      ),
    );

    const { routeParams } = getState();
    const currentCollectionId =
      routeParams.route === COLLECTION_PAGE_ROUTE
        ? routeParams.params.id
        : false;
    if (!copy && currentCollectionId) {
      const assetIds = success
        .filter((item) => item.object_type === OBJECT_TYPE_ASSETS)
        .map(({ id }) => id);
      dispatch(deleteItemsFromCollection(currentCollectionId, assetIds));
    }

    // success message
    if (responses.length) {
      let text = 'Collection updated';
      const collectionsCount = success.filter(
        (item) => item.object_type === OBJECT_TYPE_COLLECTIONS,
      ).length;
      if (copy && collectionsCount) {
        text = `Collection${
          collectionsCount > 1 ? 's' : ''
        } will be copied. This operation can take some time`;
      }
      dispatch(successMessage({ text }));
    } else {
      return false;
    }

    if (targetCollectionId !== UUID_ZERO) {
      // modify status and parents in search results
      const updatedItems = dispatch(
        updateSearchItemsAfterMoveCopy(targetCollectionId, responses, copy),
      );

      //  TODO: HERE WE SHOULD MAKE A CALL TO REGENERATE KEYFRAMES WHEN THE ENDPOINT IS READY
      // ONLY ASSETS CONTAINING KEYFRAMES
      dispatch(
        dragAndDropKeyframesSuccess({
          id: targetCollectionId,
          items: updatedItems.flatMap((updated) => {
            const item = items.find(
              (selectedItem) => selectedItem.id === updated.id,
            );
            return item ? [item] : [];
          }),
        }),
      );
    }

    return true;
  };
}

export function getCollectionParents(
  collectionId: string,
  verifyLoaded = true,
) {
  return async (dispatch: AppDispatch, getState: GetState) => {
    let currentCollection = getState().entities.collections[collectionId];

    if (!currentCollection) {
      await dispatch(httpGetCollectionsByCollectionId(collectionId));
      currentCollection = getState().entities.collections[collectionId];
    }

    // trim parents relative to the share
    const sharedCollectionId = getState().share.share.object_id;
    let parentsIds = currentCollection.parents ?? [];
    if (sharedCollectionId) {
      const rootParentIndex = parentsIds.indexOf(sharedCollectionId);
      if (rootParentIndex >= 0) {
        parentsIds = parentsIds.slice(0, rootParentIndex);
      } else {
        parentsIds = [];
      }
    }
    // check if the parents are loaded to skip the request or force the request
    if (verifyLoaded) {
      const parentObjects: AssetsCollectionSchemaWithIdNullable[] = [];
      const collections = getState().entities.collections;
      parentsIds.forEach((id) => {
        if (collections[id]) {
          parentObjects.push(collections[id]);
        }
      });
      // if all parents are loaded skip the request
      if (parentObjects.length === parentsIds.length) {
        return parentObjects;
      }
    }

    const ancestorResponse = await dispatch(
      httpGetCollectionsByCollectionIdAncestors(collectionId),
    );
    // skip parents with no permissions
    return ancestorResponse.body.objects.filter(
      ({ status }) => status !== undefined,
    );
  };
}
